import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  BrainCircuit,
  Download,
  Upload,
  Server,
  FileCode2,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  ExternalLink,
  Settings,
  MessageSquare,
  FileSpreadsheet,
  FileText,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function OdooAIModule() {
  const { token } = useAuth();
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState("");
  const [moduleFiles, setModuleFiles] = useState([]);
  const [servers, setServers] = useState([]);
  const [instances, setInstances] = useState([]);
  const [selectedServer, setSelectedServer] = useState("");
  const [selectedInstance, setSelectedInstance] = useState("");
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchVersions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/odoo-ai/versions`, { headers });
      const data = await res.json();
      setVersions(data.versions || []);
      if (data.versions?.length > 0 && !selectedVersion) {
        setSelectedVersion(data.versions[0].version);
      }
    } catch {
      toast.error("Failed to load module versions");
    }
  }, [token]);

  const fetchServers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/servers`, { headers });
      const data = await res.json();
      setServers(data.filter((s) => s.ssh_configured && s.os_type === "linux"));
    } catch {
      toast.error("Failed to load servers");
    }
  }, [token]);

  useEffect(() => {
    Promise.all([fetchVersions(), fetchServers()]).then(() => setLoading(false));
  }, [fetchVersions, fetchServers]);

  useEffect(() => {
    if (!selectedVersion) return;
    fetch(`${API}/api/odoo-ai/module-files/${selectedVersion}`, { headers })
      .then((r) => r.json())
      .then((d) => setModuleFiles(d.files || []))
      .catch(() => {});
  }, [selectedVersion, token]);

  useEffect(() => {
    if (!selectedServer) {
      setInstances([]);
      return;
    }
    fetch(`${API}/api/infrastructure/${selectedServer}`, { headers })
      .then((r) => r.json())
      .then((d) => {
        const odooInst = d.odoo_instances || [];
        setInstances(odooInst);
        if (odooInst.length > 0) setSelectedInstance(odooInst[0].id);
      })
      .catch(() => setInstances([]));
  }, [selectedServer, token]);

  const handleDownload = async () => {
    if (!selectedVersion) return;
    try {
      const res = await fetch(`${API}/api/odoo-ai/download/${selectedVersion}`, { headers });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stancor_ai_reports_${selectedVersion}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Module downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  const handleDeploy = async () => {
    if (!selectedServer || !selectedInstance || !selectedVersion) {
      toast.error("Select a server, instance, and version");
      return;
    }
    setDeploying(true);
    setDeployResult(null);
    try {
      const res = await fetch(
        `${API}/api/odoo-ai/deploy/${selectedServer}/${selectedInstance}`,
        {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ version: selectedVersion }),
        }
      );
      const data = await res.json();
      if (res.ok) {
        setDeployResult({ success: true, ...data });
        toast.success("Module deployed successfully!");
      } else {
        setDeployResult({ success: false, error: data.detail });
        toast.error(data.detail || "Deploy failed");
      }
    } catch (e) {
      setDeployResult({ success: false, error: e.message });
      toast.error("Deploy failed");
    } finally {
      setDeploying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="odoo-ai-module-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <BrainCircuit className="w-7 h-7 text-indigo-400" />
            Odoo AI Reports Module
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Deploy the AI-powered reporting module to your Odoo instances
          </p>
        </div>
      </div>

      {/* Module Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-indigo-500/10">
                <MessageSquare className="w-5 h-5 text-indigo-400" />
              </div>
              <span className="text-sm font-medium text-white">Natural Language Chat</span>
            </div>
            <p className="text-xs text-zinc-500">
              Ask "What is monthly dispatch?" and get instant AI analysis from your Odoo data.
            </p>
          </CardContent>
        </Card>
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
              </div>
              <span className="text-sm font-medium text-white">Branded Reports</span>
            </div>
            <p className="text-xs text-zinc-500">
              Auto-generates Excel & PDF reports with your company logo and branding from Odoo settings.
            </p>
          </CardContent>
        </Card>
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Settings className="w-5 h-5 text-amber-400" />
              </div>
              <span className="text-sm font-medium text-white">Self-Contained</span>
            </div>
            <p className="text-xs text-zinc-500">
              Configure your OpenAI API key inside Odoo's settings. No external dependencies.
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Download & File Browser */}
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-white flex items-center gap-2">
              <FileCode2 className="w-4 h-4 text-zinc-400" />
              Module Package
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Select value={selectedVersion} onValueChange={setSelectedVersion}>
                <SelectTrigger className="w-48 bg-zinc-900 border-white/10 text-white" data-testid="version-select">
                  <SelectValue placeholder="Select version" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {versions.map((v) => (
                    <SelectItem key={v.version} value={v.version} className="text-white">
                      {v.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={!selectedVersion}
                className="border-white/10 text-zinc-300 hover:text-white"
                data-testid="download-module-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                Download .zip
              </Button>
            </div>

            {/* File list */}
            <div className="bg-zinc-900/50 rounded-lg p-3 max-h-64 overflow-y-auto">
              <p className="text-xs text-zinc-500 mb-2">
                Module files ({moduleFiles.length}):
              </p>
              {moduleFiles.map((f) => (
                <div key={f} className="flex items-center gap-2 py-1 text-xs font-mono text-zinc-400">
                  <FileText className="w-3 h-3 text-zinc-600 flex-shrink-0" />
                  {f}
                </div>
              ))}
            </div>

            {/* Key differences note */}
            <div className="bg-indigo-500/5 border border-indigo-500/10 rounded-lg p-3">
              <p className="text-xs text-indigo-300 font-medium mb-1">Version Differences:</p>
              <ul className="text-xs text-zinc-400 space-y-1">
                <li><strong>v13:</strong> account.invoice, get_values/set_values settings, jQuery widgets</li>
                <li><strong>v19:</strong> account.move, config_parameter settings, OWL 3.0, Python 3.12+</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Deploy to Odoo */}
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Upload className="w-4 h-4 text-zinc-400" />
              Deploy to Odoo Instance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <label className="text-xs text-zinc-500 block mb-1">Server</label>
                <Select value={selectedServer} onValueChange={setSelectedServer}>
                  <SelectTrigger className="bg-zinc-900 border-white/10 text-white" data-testid="server-select">
                    <SelectValue placeholder="Select SSH server" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {servers.map((s) => (
                      <SelectItem key={s.id} value={s.id} className="text-white">
                        <span className="flex items-center gap-2">
                          <Server className="w-3 h-3" />
                          {s.name} ({s.ip_address})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs text-zinc-500 block mb-1">Odoo Instance</label>
                <Select value={selectedInstance} onValueChange={setSelectedInstance} disabled={!instances.length}>
                  <SelectTrigger className="bg-zinc-900 border-white/10 text-white" data-testid="instance-select">
                    <SelectValue placeholder={instances.length ? "Select instance" : "Select a server first"} />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {instances.map((inst) => (
                      <SelectItem key={inst.id} value={inst.id} className="text-white">
                        {inst.subdomain || inst.service_name} (:{inst.http_port})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs text-zinc-500 block mb-1">Module Version</label>
                <Select value={selectedVersion} onValueChange={setSelectedVersion}>
                  <SelectTrigger className="bg-zinc-900 border-white/10 text-white" data-testid="deploy-version-select">
                    <SelectValue placeholder="Select version" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {versions.map((v) => (
                      <SelectItem key={v.version} value={v.version} className="text-white">
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={handleDeploy}
              disabled={deploying || !selectedServer || !selectedInstance || !selectedVersion}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="deploy-btn"
            >
              {deploying ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deploying...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Deploy Module</>
              )}
            </Button>

            {/* Deploy Result */}
            {deployResult && (
              <div
                className={`rounded-lg p-4 text-sm ${
                  deployResult.success
                    ? "bg-emerald-500/10 border border-emerald-500/20"
                    : "bg-red-500/10 border border-red-500/20"
                }`}
                data-testid="deploy-result"
              >
                <div className="flex items-center gap-2 mb-2">
                  {deployResult.success ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                  )}
                  <span className={deployResult.success ? "text-emerald-300" : "text-red-300"}>
                    {deployResult.success ? "Deployed Successfully" : "Deploy Failed"}
                  </span>
                </div>
                {deployResult.message && (
                  <p className="text-zinc-400 text-xs">{deployResult.message}</p>
                )}
                {deployResult.error && (
                  <p className="text-red-400 text-xs">{deployResult.error}</p>
                )}
                {deployResult.next_step && (
                  <div className="mt-2 flex items-start gap-2 text-xs text-indigo-300">
                    <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    {deployResult.next_step}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Setup Instructions */}
      <Card className="bg-[#0c0c0e] border-white/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-white">Setup Instructions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { step: 1, title: "Deploy", desc: "Select your Odoo instance above and click Deploy. The module files are uploaded via SSH." },
              { step: 2, title: "Install in Odoo", desc: "Go to Odoo > Apps > Update Apps List, then search 'AI Reports' and click Install." },
              { step: 3, title: "Configure API Key", desc: "In Odoo, go to Settings > General Settings > AI Reports. Enter your OpenAI API key." },
              { step: 4, title: "Start Chatting", desc: "Open the 'AI Reports' menu in Odoo. Create a new chat and ask about your business data!" },
            ].map((s) => (
              <div key={s.step} className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-indigo-400">{s.step}</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-white">{s.title}</p>
                  <p className="text-xs text-zinc-500 mt-1">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Capabilities */}
      <Card className="bg-[#0c0c0e] border-white/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-white">What Users Can Ask in Odoo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              "What is the monthly dispatch?",
              "Show me this month's sales total",
              "List all pending purchase orders",
              "How many invoices are open?",
              "Give me manufacturing orders for this week in Excel",
              "Top 10 customers by sales amount in PDF",
              "What products are low on stock?",
              "Show confirmed sales orders from last month",
            ].map((q, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5 px-3 rounded-lg bg-zinc-900/50 text-xs">
                <MessageSquare className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                <span className="text-zinc-300 italic">"{q}"</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
